<script setup lang="ts">
  import { ChatState } from '~/models/chat';
  import { useChat } from '~/composables/useChat';
  import { ChatDislikeEmoji, ChatLikeEmoji, ChatRegenerateEmoji } from '@/utils/content-utils';

  const chatStore = useChatStore();
  const { chatBgImage, chatState, isChameleonMode, isChatTermsAccepted } = storeToRefs(chatStore);
  const delayedChatBgImage = ref(chatBgImage.value);

  const { messages, starterBlockData, panicMode, panicModeText, canWrite, connect, disconnect, sendMessage, sendReaction, removeMessage } = useChat();

  const send = async (message: string) => {
    if (panicMode.value) return;

    const finalMessage = message.trim();

    await sendMessage(finalMessage);
  };

  const like = async (messageId: string) => {
    if (panicMode.value) return;

    await sendReaction(messageId, ChatLikeEmoji);
  };

  const dislike = async (messageId: string) => {
    if (panicMode.value) return;

    await sendReaction(messageId, ChatDislikeEmoji);
  };

  const regenerate = async (messageId: string) => {
    if (panicMode.value) return;

    removeMessage(messageId);
    await sendReaction(messageId, ChatRegenerateEmoji);
  };

  const acceptWebsite = async () => {
    await chatStore.acceptWebsite();
  };

  const declineWebsite = async () => {
    await chatStore.declineWebsite();
  };

  onUnmounted(() => {
    disconnect();
  });

  watch(
    isChatTermsAccepted,
    async (isAccepted) => {
      if (isAccepted) {
        chatState.value = ChatState.Splash;
        await connect();

        setTimeout(() => {
          chatState.value = ChatState.Home;
        }, 2000);
      }
    },
    { immediate: true }
  );

  watch(chatBgImage, () => {
    setTimeout(() => {
      delayedChatBgImage.value = chatBgImage.value;
    }, 1000);
  });
</script>

<template>
  <section
    :class="['chat-section', { 'chat-section--chameleon': isChameleonMode, 'chat-section--default': !isChameleonMode }]"
    :style="{ '--chat-bg-image': delayedChatBgImage ? `url(${delayedChatBgImage})` : 'none' }">
    <div class="container">
      <div class="chat-section__wrapper">
        <div class="chat-section__bg">
          <ChatSplashScreen v-if="chatState === ChatState.Splash" />
          <ChatPanicScreen
            v-else-if="panicMode"
            :panic-mode-text="panicModeText" />
          <ChatHomeScreen
            v-else-if="chatState === ChatState.Home"
            :starter-blocks="starterBlockData"
            :is-panic-mode="panicMode"
            :panic-mode-text="panicModeText"
            :can-write="canWrite"
            @send="send" />
          <ChatScreen
            v-else-if="chatState === ChatState.Chat"
            :messages="messages"
            :can-write="canWrite"
            @send="send"
            @like="like"
            @dislike="dislike"
            @regenerate="regenerate"
            @accept-website="acceptWebsite"
            @decline-website="declineWebsite" />
        </div>
      </div>
    </div>
  </section>
</template>
